/**
 * The ONE place a person's display name is composed for lead surfaces.
 *
 * Leads captured by the AI chat (and abandoned sign-ups) often have no first
 * or last name. `${first} ${last}` on those printed "null null" on every Lead
 * Pipeline card, which read as deleted records (Chris, 2026-10-07). Compose a
 * name here instead of with a template literal, and a missing part can never
 * print as the word "null" or "undefined".
 */

type NamePart = string | null | undefined;

export interface NamedPerson {
  first_name?: NamePart;
  last_name?: NamePart;
  email?: NamePart;
  phone?: NamePart;
}

const part = (value: unknown): string => (typeof value === "string" ? value.trim() : "");

/** "First Last", "First", "Last" or "" — never "null". */
export function joinName(first?: unknown, last?: unknown): string {
  return [part(first), part(last)].filter(Boolean).join(" ");
}

/**
 * Name, else email, else phone, else `fallback`.
 */
export function formatPersonName(person: NamedPerson | null | undefined, fallback = ""): string {
  if (!person) return fallback;
  return joinName(person.first_name, person.last_name) || part(person.email) || part(person.phone) || fallback;
}

export const UNNAMED_LEAD = "Unnamed lead";

/** What to call a lead anywhere it is shown: name → email → phone → "Unnamed lead". */
export function leadDisplayName(lead: NamedPerson | null | undefined): string {
  return formatPersonName(lead, UNNAMED_LEAD);
}

/** True when the title shown is a real name (so email can be shown beside it without repeating). */
export function hasName(person: NamedPerson | null | undefined): boolean {
  return !!person && joinName(person.first_name, person.last_name) !== "";
}
